import { Card } from '../../../../components/ui/Card';
import { useVisibilityBrands, useVisibilityTags, useVisibilityEngines } from '../../../../hooks/useVisibility';
import { useCitationProbes } from '../../../../hooks/useMeasurement';
import type { MeasurementPage } from '../shell-types';
import { CHART_SEMANTIC } from '../../../../components/ui/chart';

interface OnboardingItem {
  label: string;
  done: boolean;
  hint: string;
  goto: MeasurementPage;
}

interface Props {
  onGoto: (page: MeasurementPage) => void;
}

export function OnboardingStrip({ onGoto }: Props) {
  const { data: brands } = useVisibilityBrands();
  const { data: tags } = useVisibilityTags();
  const { data: engines } = useVisibilityEngines();
  const { data: probes } = useCitationProbes();

  const brandCount = brands?.items.length ?? 0;
  const ownBrandSet = !!brands?.items.some((b) => b.isOwn);
  const tagCount = tags?.items.length ?? 0;
  const engineCount = engines?.items.filter((e) => e.isActive).length ?? 0;
  const probeCount = probes?.probes.filter((p) => p.isActive).length ?? 0;

  const items: OnboardingItem[] = [
    { label: 'Own brand', done: ownBrandSet, hint: 'Mark one brand as "Own" so the OS knows who "we" are.', goto: 'brands' },
    { label: 'Competitors', done: brandCount >= 3, hint: 'Track at least 3 competitors to compute share-of-voice.', goto: 'brands' },
    { label: 'Prompts', done: probeCount >= 10, hint: 'Add ≥10 prompts that cover your buyer journey.', goto: 'prompts' },
    { label: 'Tags', done: tagCount >= 2, hint: 'Tag prompts by intent to drive recommendations.', goto: 'prompts' },
    { label: 'Engines', done: engineCount >= 2, hint: 'Enable at least 2 LLM engines so trends are comparable.', goto: 'engines' },
  ];

  const doneCount = items.filter((i) => i.done).length;
  if (doneCount === items.length) return null;

  return (
    <Card padding="md">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-body-sm font-medium text-text-primary">Finish setup</p>
          <p className="text-body-xs text-text-tertiary">{doneCount}/{items.length} done — accurate metrics need a complete setup.</p>
        </div>
        <div className="h-1.5 w-32 rounded-full bg-surface-secondary overflow-hidden">
          <div
            className="h-full transition-all"
            style={{
              width: `${(doneCount / items.length) * 100}%`,
              backgroundColor: CHART_SEMANTIC.positive,
            }}
          />
        </div>
      </div>
      <ul className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {items.map((item) => (
          <li key={item.label}>
            <button
              type="button"
              disabled={item.done}
              onClick={() => onGoto(item.goto)}
              className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${
                item.done
                  ? 'border-success/30 bg-success/5 cursor-default'
                  : 'border-border-light hover:border-border hover:bg-surface-secondary'
              }`}
              title={item.hint}
            >
              <div className="flex items-center gap-2">
                <span className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${item.done ? 'bg-success text-white' : 'border border-border text-text-tertiary'}`}>
                  {item.done ? '✓' : ''}
                </span>
                <span className="text-body-xs font-medium text-text-primary">{item.label}</span>
              </div>
              <p className="text-body-xs text-text-tertiary mt-1 line-clamp-1">{item.hint}</p>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
